import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LogRocket from 'logrocket';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { useSelector } from 'react-redux';

import { readFlow } from '@api/narratives';
import {
  createErrorData,
  narrativeErrorStatuses,
  narrativeFlowPendingStatuses,
  narrativeStatus,
  flowTag,
} from '@helpers';
import { Error, LoaderView, ModalV2 as Modal, PageTitle } from '@utilities';

import { TOPIC_CARD_MOTION_PROPS } from '../../pages/NarrativesChat/components/NarrativesCanvas/utilities/constants';

import NarrativeTitle from './components/NarrativeTitle';
import NarrativeTopicCard from './components/NarrativeTopicCard';
import { loaderHeader, loaderMessage } from './utilities/helpers';

import styles from './_index.module.scss';

const NarrativeFlowView = ({
  flowId,
  getTopicControls = null,
  hidePageTitle = false,
  hideTitle = false,
  onReprompt,
  pageTitleFallback = 'Narratives',
  publicToken,
  renderAfterContent,
  renderProjectTitle,
  topicIds = null,
  useWindowScroll = true,
}) => {
  const [error, setError] = useState(null);
  const [flow, setFlow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadData, setDownloadData] = useState(new Map());
  const [previewTopicId, setPreviewTopicId] = useState(null);
  const [previewTopicData, setPreviewTopicData] = useState(null);

  const previewTopic = flow?.topics?.find((topic) => topic.id === previewTopicId) || null;
  const subscribedCategories = flow?.answers?.subscribedCategories || [];

  const flowRef = useRef(flow);
  useEffect(() => {
    flowRef.current = flow;
  }, [flow]);

  const handlePreviewToggle = useCallback((id, topicData) => {
    LogRocket.track('Flow_Preview', { id: topicData?.id, topic: topicData?.topicId });
    setPreviewTopicId((prev) => {
      if (prev === id) {
        setPreviewTopicData(null);
        return null;
      }

      if (topicData) {
        setPreviewTopicData(topicData);
      }

      return id;
    });
  }, []);

  const resetPreviewTopic = useCallback(() => {
    setPreviewTopicId(null);
    setPreviewTopicData(null);
  }, []);

  const narrativeFlow = useSelector((state) =>
    state?.websocket?.flows?.find((flow) => Number(flow?.id) === Number(flowId))
  );

  const narrativeRoute = useMemo(() => {
    if (!flow) return {};
    return {
      id: flow?.narrative_id,
      name: flow?.narrativeName,
      path: `/dashboard/narrative/${flow?.narrative_id}`,
    };
  }, [flow]);

  const fetchFlow = useCallback(async () => {
    if (!flowId) return;

    try {
      const response = await readFlow({ id: flowId, publicToken });
      if (narrativeErrorStatuses.includes(response?.status)) {
        setError(createErrorData({ status: 500 }));
        setIsLoading(false);
      }
      if (response?.status === narrativeStatus.COMPLETED && flowTag.NO_SAMPLE in response?.tags) {
        setError(
          createErrorData({
            description: response?.tags[flowTag.NO_SAMPLE],
            header: 'Insufficient Sample',
            status: 'failed-flow',
          })
        );
      }
      setFlow(response);
      if (!narrativeFlowPendingStatuses.includes(response?.status)) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setError(createErrorData({ status: error?.status }));
      setIsLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    if (!flowId) return;

    setIsLoading(true);
    setError(null);
    fetchFlow();
    setPreviewTopicId(null);
    setPreviewTopicData(null);
  }, [flowId, fetchFlow]);

  useEffect(() => {
    if (!narrativeFlow) return;

    const data = narrativeFlow;

    if (narrativeFlowPendingStatuses.includes(data?.status)) {
      setFlow((prevFlow) => ({
        ...prevFlow,
        status: data?.status,
      }));
      return;
    }

    const currentFlow = flowRef.current;

    if (!currentFlow?.topics?.length) {
      fetchFlow();
      return;
    }

    const updateCompletedTopics = () => {
      const completeFlowTopicIds = new Set(data.completeFlowTopicIds);

      setFlow((prevFlow) => ({
        ...prevFlow,
        topics: prevFlow.topics?.map((topic) => {
          if (completeFlowTopicIds.has(topic.id) && topic.status !== narrativeStatus.COMPLETED) {
            return { ...topic, status: narrativeStatus.COMPLETED };
          }
          return topic;
        }),
      }));
    };

    const updateIncompleteTopics = () => {
      const incompleteFlowTopicIds = new Set(data?.incompleteFlowTopicIds);

      setFlow((prevFlow) => ({
        ...prevFlow,
        topics: prevFlow.topics?.map((topic) => {
          if (incompleteFlowTopicIds.has(topic.id) && topic.status !== narrativeStatus.EXCEPTION) {
            return { ...topic, status: narrativeStatus.EXCEPTION };
          }
          return topic;
        }),
      }));
    };

    if (narrativeErrorStatuses.includes(data?.status)) {
      setError(createErrorData({ status: 500 }));
      return;
    }

    if (data?.status === narrativeStatus.COMPLETED) {
      if (data?.tags && flowTag.NO_SAMPLE in data?.tags) {
        setError(
          createErrorData({
            description: data.tags[flowTag.NO_SAMPLE],
            header: 'Insufficient Sample',
            status: 'failed-flow',
          })
        );
      }

      setFlow((prevFlow) => ({
        ...prevFlow,
        status: narrativeStatus.COMPLETED,
      }));

      if (data?.numberIncompleteTopics?.length > 0) updateIncompleteTopics();
      if (data?.numberCompleteTopics?.length > 0) updateCompletedTopics();
    } else if (data?.completeFlowTopicIds?.length > 0) {
      updateCompletedTopics();
    }
  }, [narrativeFlow, fetchFlow]);

  const hookData = {
    downloadData,
    error,
    flow,
    handlePreviewToggle,
    isLoading,
    narrativeRoute,
    previewTopic,
    previewTopicData,
    previewTopicId,
    resetPreviewTopic,
    setDownloadData,
    setFlow,
    subscribedCategories,
  };

  if (isLoading) {
    return <LoaderView heading={loaderHeader(flow?.status)} messages={loaderMessage(flow)} />;
  }

  const errorStatus = error?.STATUS_ERROR;
  const isTitleHidden =
    hideTitle || (typeof errorStatus === 'number' && errorStatus >= 400 && errorStatus < 500);

  const handleRepromptClick = typeof onReprompt === 'function' ? onReprompt : null;

  const title = isTitleHidden ? null : (
    <NarrativeTitle
      downloadData={downloadData}
      flowData={flow}
      handleReprompt={handleRepromptClick}
      publicToken={publicToken}
      setFlow={setFlow}
      topicIds={topicIds}
    />
  );

  const body = error ? (
    <Error description={error.ERROR_DESCRIPTION} header={error.ERROR_HEADER} status={errorStatus} />
  ) : (
    <motion.div layout>
      <AnimatePresence initial={false} mode="popLayout">
        {flow?.topics?.map((topic, index) => {
          const topicControls =
            typeof getTopicControls === 'function'
              ? getTopicControls({
                  flow,
                  index,
                  setFlow,
                  topic,
                  topics: flow?.topics || [],
                })
              : null;

          return (
            <motion.div
              className={styles['narrative-flow-topic-card']}
              data-flowtopicid={topic.id}
              data-testid={topic.topicId}
              key={topic.id}
              layout
              {...TOPIC_CARD_MOTION_PROPS}
            >
              <NarrativeTopicCard
                key={topic.id}
                keyIndex={index}
                onPreview={(id, topicData) => handlePreviewToggle(id, topicData)}
                publicToken={publicToken}
                setDownloadData={setDownloadData}
                subscribedCategories={subscribedCategories}
                topic={topic}
                topicControls={topicControls}
                useWindowScroll={useWindowScroll}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <>
      {renderProjectTitle && renderProjectTitle()}
      {!hidePageTitle && <PageTitle title={flow?.name || pageTitleFallback} />}
      {title}
      {body}
      {renderAfterContent && renderAfterContent(hookData)}
      {previewTopicId && previewTopic && previewTopicData && (
        <Modal buttons={[]} isActive={previewTopicId} onClose={resetPreviewTopic} size="x-large">
          <NarrativeTopicCard
            initialTopicData={previewTopicData}
            isPreviewActive
            key={previewTopic?.id}
            keyIndex={0}
            onPreview={(id, topicData) => handlePreviewToggle(id, topicData)}
            publicToken={publicToken}
            setDownloadData={setDownloadData}
            topic={previewTopic}
          />
        </Modal>
      )}
    </>
  );
};

NarrativeFlowView.propTypes = {
  flowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  getTopicControls: PropTypes.func,
  hidePageTitle: PropTypes.bool,
  hideTitle: PropTypes.bool,
  onReprompt: PropTypes.func,
  pageTitleFallback: PropTypes.string,
  publicToken: PropTypes.string,
  renderAfterContent: PropTypes.func,
  renderProjectTitle: PropTypes.func,
  topicIds: PropTypes.array,
  useWindowScroll: PropTypes.bool,
};

export default NarrativeFlowView;
